import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import axios from "axios";
import toast from "react-hot-toast";
import {
  FaPlus,
  FaSearch,
  FaPen,
  FaTrash,
  FaUsers,
  FaPhone,
  FaEnvelope,
} from "react-icons/fa";
import styles from "./BusinessEntitiesList.module.css";

const BusinessEntitiesList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { userData, token } = useContext(UserContext);

  const [entities, setEntities] = useState([]);
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("SUPPLIER");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState(null);

  useEffect(() => {
    fetchBusinessEntities();
  }, []);

  useEffect(() => {
    filterEntities();
  }, [entities, selectedType, searchTerm]);

  const fetchBusinessEntities = async () => {
    setLoading(true);
    try {
      // Fetch both suppliers and clients separately as per Android implementation
      const [suppliersResponse, clientsResponse] = await Promise.all([
        axios.get(`api/supplier/findByFarmId/${userData?.Farm?.id}`, {
          headers: { Authorization: token },
        }),
        axios.get(`api/client/findByFarmId/${userData?.Farm?.id}`, {
          headers: { Authorization: token },
        }),
      ]);
      console.log("suppliers", suppliersResponse.data.items);
      console.log("clients", clientsResponse.data.items);

      // Combine and format the data
      const suppliers =
        suppliersResponse.data.items?.map((item) => ({
          ...item,
          accountType: "SUPPLIER",
        })) || [];

      const clients =
        clientsResponse.data.items?.map((item) => ({
          ...item,
          accountType: "CLIENT",
        })) || [];

      // Merge both lists and remove duplicates based on accountId (entities that are BOTH)
      const allEntities = [...suppliers, ...clients];
      const uniqueEntities = allEntities.reduce((acc, current) => {
        const existingEntity = acc.find((item) => item.accountId === current.accountId);
        if (existingEntity) {
          // If entity exists in both lists, mark it as BOTH
          existingEntity.accountType = "BOTH";
        } else {
          acc.push(current);
        }
        return acc;
      }, []);

      setEntities(uniqueEntities);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching business entities:", error);
      toast.error(t("Error fetching business entities"));
      setLoading(false);
    }
  };

  const filterEntities = () => {
    let filtered = entities;

    // Filter by type (SUPPLIER, CLIENT, or show BOTH for either)
    if (selectedType === "SUPPLIER") {
      filtered = entities.filter(
        (entity) =>
          entity.accountType === "SUPPLIER" || entity.accountType === "BOTH"
      );
    } else if (selectedType === "CLIENT") {
      filtered = entities.filter(
        (entity) =>
          entity.accountType === "CLIENT" || entity.accountType === "BOTH"
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (entity) =>
          entity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          entity.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          entity.phone?.includes(searchTerm)
      );
    }

    setFilteredEntities(filtered);
  };

  const handleAddNew = () => {
    navigate("/business-entities/new");
  };

  const handleEdit = (e, entity) => {
    e.stopPropagation();
    navigate(`/business-entities/edit/${entity.accountId}`, { state: { entity } });
  };

  const handleDeleteClick = (e, entity) => {
    e.stopPropagation();
    setEntityToDelete(entity);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!entityToDelete) return;

    try {
      // Match Android API: entitiesAccounts/deleteEntityAccount/{id}
      await axios.delete(
        `api/entitiesAccounts/deleteEntityAccount/${entityToDelete.accountId || entityToDelete.id}`,
        {
          headers: { Authorization: token },
        }
      );

      // Remove from local state
      setEntities(entities.filter((e) => e.id !== entityToDelete.id));
      toast.success(t("Entity deleted successfully"));
      setShowDeleteModal(false);
      setEntityToDelete(null);
    } catch (error) {
      console.error("Error deleting entity:", error);
      toast.error(t("Error deleting entity"));
    }
  };

  const handleRowClick = (entity) => {
    navigate(`/business-entities/profile/${entity.id}`, { state: { entity } });
  };

  const getEntityTypeBadge = (type) => {
    const badges = {
      SUPPLIER: { text: t("Supplier"), className: styles.supplierBadge },
      CLIENT: { text: t("Client"), className: styles.clientBadge },
      BOTH: { text: t("Both"), className: styles.bothBadge },
    };
    return badges[type] || badges.CLIENT;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(i18n.language === "ar" ? "ar-EG" : "en-US", {
      style: "currency",
      currency: "EGP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1>{t("Suppliers & Clients")}</h1>
          <p className={styles.entityCount}>
            {filteredEntities.length} {t("entities found")}
          </p>
        </div>
        <button className={styles.addButton} onClick={handleAddNew}>
          <FaPlus /> {t("Add New")}
        </button>
      </div>

      <div className={styles.filterButtons}>
        <button
          className={`${styles.filterButton} ${
            selectedType === "SUPPLIER" ? styles.active : ""
          }`}
          onClick={() => setSelectedType("SUPPLIER")}
        >
          {t("Suppliers")}
        </button>
        <button
          className={`${styles.filterButton} ${
            selectedType === "CLIENT" ? styles.active : ""
          }`}
          onClick={() => setSelectedType("CLIENT")}
        >
          {t("Clients")}
        </button>
      </div>

      <div className={styles.controlsSection}>
        <div className={styles.searchBox}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            placeholder={t("Search by name, email, or phone...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>{t("Loading...")}</p>
        </div>
      ) : filteredEntities.length === 0 ? (
        <div className={styles.emptyState}>
          <FaUsers className={styles.emptyIcon} />
          <h3>{t("No entities found")}</h3>
          <p>
            {searchTerm
              ? t("Try adjusting your search criteria")
              : t("Click 'Add New' to create your first entity")}
          </p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t("Name")}</th>
                <th>{t("Type")}</th>
                <th>{t("Phone")}</th>
                <th>{t("Email")}</th>
                <th>{t("Initial Balance")}</th>
                <th>{t("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntities.map((entity) => (
                <tr
                  key={entity.id}
                  onClick={() => handleRowClick(entity)}
                  className={styles.tableRow}
                >
                  <td>
                    <div className={styles.nameCell}>
                      <span className={styles.entityName}>{entity.name}</span>
                      {entity.address && (
                        <span className={styles.entityAddress}>
                          {entity.address}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span
                      className={
                        getEntityTypeBadge(entity.accountType).className
                      }
                    >
                      {getEntityTypeBadge(entity.accountType).text}
                    </span>
                  </td>
                  <td>
                    <div className={styles.contactCell}>
                      <FaPhone className={styles.contactIcon} />
                      {entity.phone || "—"}
                    </div>
                  </td>
                  <td>
                    <div className={styles.contactCell}>
                      <FaEnvelope className={styles.contactIcon} />
                      {entity.email || "—"}
                    </div>
                  </td>
                  <td>
                    <span
                      className={`${styles.balance} ${
                        entity.initialBalance && entity.initialBalance < 0
                          ? styles.negative
                          : styles.positive
                      }`}
                    >
                      {entity.initialBalance != null ? formatCurrency(entity.initialBalance) : "—"}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.actionButton}
                        onClick={(e) => handleEdit(e, entity)}
                        title={t("Edit")}
                      >
                        <FaPen />
                      </button>
                      <button
                        className={`${styles.actionButton} ${styles.deleteButton}`}
                        onClick={(e) => handleDeleteClick(e, entity)}
                        title={t("Delete")}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowDeleteModal(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t("Confirm Delete")}</h3>
            <p>
              {t("Are you sure you want to delete")} "{entityToDelete?.name}"?
            </p>
            <div className={styles.modalButtons}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowDeleteModal(false)}
              >
                {t("Cancel")}
              </button>
              <button
                className={styles.confirmButton}
                onClick={handleDeleteConfirm}
              >
                {t("Delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessEntitiesList;
